import React, { Component } from "react";
import { OffScreenComponent } from "../../../../../../ui/components/mixins";
import TextareaAutosize from "react-autosize-textarea";
import styled, { css } from "styled-components";
import { cloud, ninja } from "@vitalsource/vstui/colors";
import { Image, Delete } from "@vitalsource/vstui-icons";
import { Button } from "@vitalsource/vstui";
import FigureOrImage from "../../../../../../apps/overleaf/components/figure-or-image";
import { TYPE_IMAGE, TYPE_TEXT } from "../../../../../../stores/overleaf-store";
import { getEmptyCard } from "../../types";

const commonTextStyles = css`
  width: 100%;
  min-height: 42px;
  border: 1px solid ${cloud.getShade(4)};
  border-radius: 4px;
  font-size: 1.5rem;
  font-weight: 300;
  color: ${ninja.getShade(10)};
  text-align: center;
  resize: none;
  margin: 0;
  padding: ${props => (props.hasFigures ? "0 48px" : "0 12px")};
  appearance: none;

  &[aria-invalid="true"] {
    border-color: ${props => props.theme.vstui.color.error};
  }

  &::placeholder {
    font-weight: 400;
    color: ${cloud.getShade(6)};
  }

  &::-ms-clear {
    display: none;
  }

  &:focus {
    outline: none;
    &:-moz-focusring {
      outline: none;
    }
    border: 1px solid ${props => props.theme.vstui.color.primary.getShade(6)};
    box-shadow: none;
  }
`;

const DELETE_SIZE = 24;

// Fix react console error about hasFigures being passed to dom node
// Known styled-components limitation
const WrappedTextAreaAutosize = ({ hasFigures, ...restProps }) => (
  <TextareaAutosize {...restProps} />
);

const Styled = {
  FieldContainer: styled.div`
    position: relative;
  `,
  TextArea: styled(WrappedTextAreaAutosize)`
    ${commonTextStyles};
    padding-top: 11px;
    padding-bottom: 11px;
  `,
  Input: styled.input`
    ${commonTextStyles};
    font-weight: 600;
  `,
  ErrorMessage: styled.p`
    color: ${props => props.theme.vstui.color.error};
    margin: 5px 0 0;
  `,
  ImageButtonContainerOuter: styled.div`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    // !important due to css #reader #panel-container * { pointer-events: all; }
    pointer-events: none !important;
  `,
  ImageButtonContainerInner: styled.div`
    margin-left: auto;
    pointer-events: auto;
  `,
  ImageContainerOuter: styled.div`
    text-align: center;
    max-width: 100%;
  `,
  ImageContainerInner: styled.div`
    position: relative;
    display: inline-block;
    max-width: 100%;
  `,
  CardImageStyles: css`
    border: 1px solid ${cloud.getShade(4)};
    max-width: 100%;
    max-height: 115px;
    line-height: 0;
  `,
  ImageDeleteButton: styled.button`
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(8px, -8px);
    width: ${DELETE_SIZE}px;
    height: ${DELETE_SIZE}px;
    border-radius: ${DELETE_SIZE}px;
    background-color: ${cloud.getShade(6)};
    appearance: none;
    color: white;
    border: none;
    box-shadow: none;
    padding: 0;
    svg {
      display: block;
      width: 12px;
      height: 13.1px;
      margin: auto;
    }
    &:hover {
      background-color: ${cloud.getShade(7)};
    }
    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px white, 0 0 0 4px ${cloud.getShade(7)};
      background-color: ${cloud.getShade(7)};
    }
  `
};

export default class CardInput extends Component {
  inputId = "CardInput" + Math.random();
  errorId = "CardInputError" + Math.random();

  focus() {
    if (this.container) {
      this.container.querySelector("input, textarea, button").focus();
    }
  }

  onTextChange = e => {
    this.props.onChange({
      type: TYPE_TEXT,
      content: e.currentTarget.value,
      metadata: {}
    });
  };

  removeFigure = () => {
    this.props.onChange(getEmptyCard());
  };

  render() {
    const {
      label,
      cardContent,
      error,
      singleLine,
      maxLength,
      onOpenFigures,
      hasFigures,
      addFigureLabel
    } = this.props;

    if (cardContent.type === TYPE_IMAGE) {
      const title =
        cardContent.metadata.title ||
        i18n.t("flashcards.images.description-unavailable");

      return (
        <Styled.ImageContainerOuter innerRef={el => (this.container = el)}>
          <Styled.ImageContainerInner>
            <FigureOrImage
              src={cardContent.content}
              title={cardContent.metadata.title}
              caption={cardContent.metadata.caption}
              alt={cardContent.metadata.alt}
              imageStyles={Styled.CardImageStyles}
            />
            <Styled.ImageDeleteButton
              aria-label={`${i18n.t("base.delete")} ${title}`}
              type="button"
              onClick={this.removeFigure}
            >
              <Delete />
            </Styled.ImageDeleteButton>
          </Styled.ImageContainerInner>
          {error && (
            <Styled.ErrorMessage role="alert">{error}</Styled.ErrorMessage>
          )}
        </Styled.ImageContainerOuter>
      );
    }

    const TextComponent = singleLine ? Styled.Input : Styled.TextArea;

    return (
      <div ref={el => (this.container = el)}>
        <Styled.FieldContainer>
          <OffScreenComponent>
            <label htmlFor={this.inputId}>{label}</label>
          </OffScreenComponent>
          <TextComponent
            id={this.inputId}
            aria-describedby={this.errorId}
            aria-invalid={!!error}
            type="text"
            onChange={this.onTextChange}
            placeholder={label}
            value={cardContent.content}
            async
            maxLength={maxLength}
            hasFigures={hasFigures}
          />
          {hasFigures && (
            <Styled.ImageButtonContainerOuter>
              <Styled.ImageButtonContainerInner>
                <Button
                  iconStart={<Image />}
                  naked
                  label={addFigureLabel}
                  disabled={!!cardContent.content}
                  type="button"
                  onClick={onOpenFigures}
                />
              </Styled.ImageButtonContainerInner>
            </Styled.ImageButtonContainerOuter>
          )}
        </Styled.FieldContainer>
        {error && (
          <Styled.ErrorMessage role="alert">{error}</Styled.ErrorMessage>
        )}
      </div>
    );
  }
}
